import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';


const HOVER_ANIMATION_DURATION = 400;


/**
 * Continuous Announcement Bar
 *
 * Correct order:
 *
 * 1 → 2 → 3 → 1 → 2 → 3 → 1 → 2 → 3...
 *
 * Features:
 *
 * - All announcements visible
 * - Automatically fills entire viewport
 * - No blank area
 * - No first-loop delay
 * - Correct original Shopify block order
 * - Same spacing between every announcement
 * - Seamless infinite loop
 * - Smooth stop on hover
 * - Smooth resume on mouse leave
 * - Rebuilds when browser width changes
 *
 * @extends Component
 */
export class AnnouncementBar extends Component {

  /**
   * @type {HTMLElement | null}
   */
  viewport = null;


  /**
   * @type {HTMLElement | null}
   */
  track = null;


  /**
   * The ORIGINAL group only.
   *
   * @type {HTMLElement | null}
   */
  content = null;


  /**
   * Original Shopify announcement block elements.
   *
   * These never contain generated repeat items.
   *
   * @type {HTMLElement[]}
   */
  originalItems = [];


  /**
   * Last measured viewport width.
   *
   * @type {number}
   */
  viewportWidth = 0;


  /**
   * Current smooth hover playback-rate animation.
   *
   * @type {{ cancel: () => void, current: number } | null}
   */
  rateAnimation = null;


  /**
   * Used to prevent an older build from
   * finishing after a newer build.
   *
   * @type {number}
   */
  buildId = 0;


  connectedCallback() {
    super.connectedCallback();


    this.viewport = this.querySelector(
      '.announcement-bar__viewport'
    );


    this.track = this.querySelector(
      '.announcement-bar__track'
    );


    this.content = this.querySelector(
      '.announcement-bar__group:not([data-announcement-clone])'
    );


    if (
      !this.viewport ||
      !this.track ||
      !this.content
    ) {
      return;
    }


    /*
     * Remove anything generated by a previous
     * Theme Editor connection.
     */
    this.cleanupGenerated();


    /*
     * Store ONLY real Shopify blocks.
     *
     * Example:
     *
     * [
     *   Free Shipping,
     *   Summer Sale,
     *   Welcome
     * ]
     */
    this.originalItems = Array.from(
      this.content.children
    );


    if (
      this.originalItems.length === 0
    ) {
      return;
    }


    /*
     * Remove old order styles that may have been
     * added by previous marquee code.
     *
     * This is critical.
     *
     * Otherwise:
     *
     * 1 2 3 1 2 3
     *
     * can become:
     *
     * 1 1 1 2 2 2 3 3 3
     */
    this.removeOrderStyles(
      this.content
    );


    /*
     * Make ALL original announcements visible.
     */
    this.showAllAnnouncements(
      this.content
    );


    this.setupEvents();


    /*
     * Build immediately.
     *
     * We intentionally do NOT wait for fonts here.
     *
     * This means the customer doesn't have to wait
     * before all marquee copies are generated.
     */
    this.buildMarquee();


    /*
     * If fonts are still loading, rebuild one time
     * after they finish.
     *
     * Font width can affect how many copies are needed.
     */
    if (
      document.fonts &&
      document.fonts.status !== 'loaded'
    ) {

      document.fonts.ready
        .then(() => {

          if (
            this.isConnected
          ) {
            this.buildMarquee();
          }

        })
        .catch(() => {
          /*
           * Do nothing.
           *
           * The first build already works.
           */
        });

    }
  }


  disconnectedCallback() {

    this.rateAnimation?.cancel();


    window.removeEventListener(
      'resize',
      this.handleResize
    );


    this.removeEventListener(
      'pointerenter',
      this.pauseSmoothly
    );


    this.removeEventListener(
      'pointerleave',
      this.resumeSmoothly
    );


    this.removeEventListener(
      'focusin',
      this.pauseSmoothly
    );


    this.removeEventListener(
      'focusout',
      this.handleFocusOut
    );


    document.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );


    super.disconnectedCallback?.();
  }


  /**
   * Register events.
   */
  setupEvents() {

    window.addEventListener(
      'resize',
      this.handleResize
    );


    /*
     * Pointer works for mouse/pen.
     *
     * Desktop:
     * hover = smoothly stop
     */
    this.addEventListener(
      'pointerenter',
      this.pauseSmoothly
    );


    this.addEventListener(
      'pointerleave',
      this.resumeSmoothly
    );


    /*
     * Accessibility:
     * pause while keyboard focus is inside.
     */
    this.addEventListener(
      'focusin',
      this.pauseSmoothly
    );


    this.addEventListener(
      'focusout',
      this.handleFocusOut
    );


    /*
     * Pause when browser tab isn't visible.
     */
    document.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange
    );

  }


  /**
   * Remove all generated content.
   *
   * Keeps the ORIGINAL Shopify blocks.
   */
  cleanupGenerated() {

    if (
      !this.track ||
      !this.content
    ) {
      return;
    }


    /*
     * Remove complete duplicated group.
     */
    this.track
      .querySelectorAll(
        '[data-announcement-clone]'
      )
      .forEach((element) => {

        element.remove();

      });


    /*
     * Remove repeated items inside original group.
     */
    this.content
      .querySelectorAll(
        '[data-announcement-repeat]'
      )
      .forEach((element) => {

        element.remove();

      });

  }


  /**
   * Remove inline CSS order values.
   *
   * This guarantees actual DOM order.
   *
   * @param {HTMLElement} container
   */
  removeOrderStyles(container) {

    Array.from(
      container.children
    ).forEach((element) => {

      element.style.removeProperty(
        'order'
      );

    });


    container
      .querySelectorAll(
        '.announcement-bar__slide'
      )
      .forEach((slide) => {

        slide.style.removeProperty(
          'order'
        );

      });

  }


  /**
   * Force Horizon announcement blocks visible.
   *
   * Horizon normally hides inactive slideshow items.
   *
   * @param {HTMLElement} container
   */
  showAllAnnouncements(container) {

    container
      .querySelectorAll(
        '.announcement-bar__slide'
      )
      .forEach((slide) => {

        slide.style.opacity =
          '1';


        slide.style.visibility =
          'visible';


        slide.style.display =
          'flex';


        slide.style.removeProperty(
          'order'
        );

      });

  }


  /**
   * Main marquee builder.
   */
  async buildMarquee() {

    if (
      !this.viewport ||
      !this.track ||
      !this.content
    ) {
      return;
    }


    const currentBuildId =
      ++this.buildId;


    /*
     * Temporarily stop the animation during rebuild.
     *
     * Original text is still visible.
     *
     * We are NOT hiding the track.
     */
    this.removeAttribute(
      'data-marquee-ready'
    );


    /*
     * Cancel any old hover transition.
     */
    this.rateAnimation?.cancel();

    this.rateAnimation = null;


    /*
     * Restore the content to ORIGINAL announcements.
     */
    this.cleanupGenerated();


    this.removeOrderStyles(
      this.content
    );


    this.showAllAnnouncements(
      this.content
    );


    /*
     * Wait one frame for browser layout.
     */
    await nextFrame();


    if (
      currentBuildId !== this.buildId ||
      !this.isConnected
    ) {
      return;
    }


    /*
     * Width available to customer.
     */
    const viewportWidth =
      this.viewport
        .getBoundingClientRect()
        .width;


    if (
      viewportWidth <= 0
    ) {
      return;
    }


    this.viewportWidth =
      viewportWidth;


    /*
     * Width of ONE original sequence.
     *
     * Example:
     *
     * Free Shipping
     *       gap
     * Summer Sale
     *       gap
     * Welcome
     *       final trailing gap
     */
    const originalSequenceWidth =
      this.content
        .getBoundingClientRect()
        .width;


    if (
      originalSequenceWidth <= 0
    ) {
      return;
    }


    /*
     * Make Group A wider than the viewport.
     *
     * 1.35 gives additional safety.
     *
     * This prevents any blank portion
     * while scrolling.
     */
    const minimumContentWidth =
      viewportWidth * 1.35;


    /*
     * How many COMPLETE sequences are needed?
     *
     * Example:
     *
     * viewport = 2000px
     * original sequence = 700px
     *
     * ceil(2700 / 700)
     * = 4 sequences
     *
     * Final:
     *
     * 1 2 3
     * 1 2 3
     * 1 2 3
     * 1 2 3
     */
    const sequenceCopies =
      Math.max(
        1,
        Math.ceil(
          minimumContentWidth /
          originalSequenceWidth
        )
      );


    /*
     * Add the additional complete sequences.
     *
     * VERY IMPORTANT:
     *
     * We repeat:
     *
     * 1 → 2 → 3
     * 1 → 2 → 3
     * 1 → 2 → 3
     *
     * NOT:
     *
     * 1 → 1 → 1
     * 2 → 2 → 2
     * 3 → 3 → 3
     */
    for (
      let copyIndex = 1;
      copyIndex < sequenceCopies;
      copyIndex++
    ) {

      const fragment =
        document.createDocumentFragment();


      this.originalItems.forEach(
        (originalItem) => {

          const repeatedItem =
            originalItem.cloneNode(true);


          repeatedItem.setAttribute(
            'data-announcement-repeat',
            ''
          );


          repeatedItem.setAttribute(
            'aria-hidden',
            'true'
          );


          this.prepareGeneratedItem(
            repeatedItem
          );


          fragment.appendChild(
            repeatedItem
          );

        }
      );


      this.content.appendChild(
        fragment
      );

    }


    /*
     * Once again remove possible inherited
     * order values.
     */
    this.removeOrderStyles(
      this.content
    );


    this.showAllAnnouncements(
      this.content
    );


    /*
     * Wait for final sequence layout.
     */
    await nextFrame();


    if (
      currentBuildId !== this.buildId ||
      !this.isConnected
    ) {
      return;
    }


    /*
     * Group A is now full.
     *
     * Example:
     *
     * A =
     * 1 2 3 1 2 3 1 2 3
     *
     * Now clone ALL of A.
     *
     * B =
     * 1 2 3 1 2 3 1 2 3
     *
     * Track:
     *
     * A | B
     */
    const completeClone =
      this.content.cloneNode(true);


    completeClone.setAttribute(
      'data-announcement-clone',
      ''
    );


    completeClone.setAttribute(
      'aria-hidden',
      'true'
    );


    completeClone.removeAttribute(
      'ref'
    );


    /*
     * Remove any possible order values from B.
     */
    this.removeOrderStyles(
      completeClone
    );


    this.showAllAnnouncements(
      completeClone
    );


    /*
     * Remove duplicate IDs and disable
     * duplicate keyboard controls.
     */
    this.prepareCloneTree(
      completeClone
    );


    this.track.appendChild(
      completeClone
    );


    /*
     * Calculate the duration based on
     * actual content width.
     *
     * This keeps visual speed similar even
     * when more repeated sequences are required.
     */
    const completeGroupWidth =
      this.content
        .getBoundingClientRect()
        .width;


    const baseSeconds =
      Number(
        this.getAttribute(
          'data-speed-base'
        )
      ) || 30;


    /*
     * baseSeconds means approximately how long
     * one viewport-width of movement should take.
     */
    const widthRatio =
      Math.max(
        1,
        completeGroupWidth /
        viewportWidth
      );


    const animationDuration =
      baseSeconds *
      widthRatio;


    this.style.setProperty(
      '--announcement-speed',
      `${animationDuration}s`
    );


    /*
     * Wait one final frame.
     */
    await nextFrame();


    if (
      currentBuildId !== this.buildId ||
      !this.isConnected
    ) {
      return;
    }


    /*
     * NOW begin animation.
     *
     * At this point the track already contains:
     *
     * 1 2 3 1 2 3 1 2 3 |
     * 1 2 3 1 2 3 1 2 3
     */
    this.setAttribute(
      'data-marquee-ready',
      ''
    );


    /*
     * Wait until CSS animation exists.
     */
    requestAnimationFrame(() => {

      const animation =
        this.getTrackAnimation();


      if (!animation) {
        return;
      }


      /*
       * Start from normal speed.
       */
      animation.updatePlaybackRate(
        1
      );


      /*
       * If mouse was already over the bar
       * while JS was building, keep it stopped.
       */
      if (
        this.matches(':hover')
      ) {

        animation.updatePlaybackRate(
          0
        );

      }

    });

  }


  /**
   * Prepare a repeated announcement item.
   *
   * @param {HTMLElement} element
   */
  prepareGeneratedItem(element) {

    /*
     * Never inherit flex order.
     */
    element.style.removeProperty(
      'order'
    );


    /*
     * Remove duplicate IDs from root.
     */
    if (
      element.hasAttribute('id')
    ) {

      element.removeAttribute(
        'id'
      );

    }


    /*
     * Remove duplicate IDs inside.
     */
    element
      .querySelectorAll('[id]')
      .forEach((child) => {

        child.removeAttribute(
          'id'
        );

      });


    /*
     * Force all slides visible.
     */
    element
      .querySelectorAll(
        '.announcement-bar__slide'
      )
      .forEach((slide) => {

        slide.style.removeProperty(
          'order'
        );


        slide.style.opacity =
          '1';


        slide.style.visibility =
          'visible';


        slide.style.display =
          'flex';

      });


    /*
     * This is a repeated visual copy.
     *
     * Prevent duplicate keyboard focus.
     */
    element
      .querySelectorAll(
        `
          a,
          button,
          input,
          select,
          textarea,
          [tabindex]
        `
      )
      .forEach((interactive) => {

        interactive.setAttribute(
          'tabindex',
          '-1'
        );

      });

  }


  /**
   * Prepare the complete second group.
   *
   * @param {HTMLElement} container
   */
  prepareCloneTree(container) {

    /*
     * Remove duplicate IDs.
     */
    if (
      container.hasAttribute('id')
    ) {

      container.removeAttribute(
        'id'
      );

    }


    container
      .querySelectorAll('[id]')
      .forEach((element) => {

        element.removeAttribute(
          'id'
        );

      });


    /*
     * Never inherit old order values.
     */
    this.removeOrderStyles(
      container
    );


    /*
     * Disable duplicated interactive elements.
     */
    container
      .querySelectorAll(
        `
          a,
          button,
          input,
          select,
          textarea,
          [tabindex]
        `
      )
      .forEach((element) => {

        element.setAttribute(
          'tabindex',
          '-1'
        );

      });

  }


  /**
   * Return marquee CSS animation.
   */
  getTrackAnimation() {

    if (
      !this.track
    ) {
      return null;
    }


    const animations =
      this.track.getAnimations();


    return (
      animations.find(
        (animation) => {

          const effect =
            animation.effect;


          return (
            effect &&
            effect.target ===
              this.track
          );

        }
      ) ??
      animations[0] ??
      null
    );

  }


  /**
   * Smoothly stop marquee on hover.
   */
  pauseSmoothly = () => {

    const animation =
      this.getTrackAnimation();


    if (!animation) {
      return;
    }


    /*
     * Make sure CSS animation itself
     * is running before controlling rate.
     */
    animation.play();


    const currentRate =
      this.rateAnimation?.current ??
      animation.playbackRate ??
      1;


    this.rateAnimation?.cancel();


    this.rateAnimation =
      animateValue({

        from:
          currentRate,

        to:
          0,

        duration:
          HOVER_ANIMATION_DURATION,

        onUpdate:
          (value) => {

            animation.updatePlaybackRate(
              Math.max(
                0,
                value
              )
            );

          },

        onComplete:
          () => {

            animation.updatePlaybackRate(
              0
            );


            this.rateAnimation =
              null;

          },

      });

  };


  /**
   * Smoothly resume marquee.
   */
  resumeSmoothly = () => {

    if (
      document.hidden
    ) {
      return;
    }


    const animation =
      this.getTrackAnimation();


    if (!animation) {
      return;
    }


    animation.play();


    const currentRate =
      this.rateAnimation?.current ??
      animation.playbackRate ??
      0;


    /*
     * Already normal speed.
     */
    if (
      currentRate >= 0.999
    ) {

      animation.updatePlaybackRate(
        1
      );


      return;

    }


    this.rateAnimation?.cancel();


    this.rateAnimation =
      animateValue({

        from:
          currentRate,

        to:
          1,

        duration:
          HOVER_ANIMATION_DURATION,

        onUpdate:
          (value) => {

            animation.updatePlaybackRate(
              Math.min(
                1,
                Math.max(
                  0,
                  value
                )
              )
            );

          },

        onComplete:
          () => {

            animation.updatePlaybackRate(
              1
            );


            this.rateAnimation =
              null;

          },

      });

  };


  /**
   * Rebuild when desktop/mobile width changes.
   */
  handleResize = debounce(
    () => {

      if (
        !this.viewport
      ) {
        return;
      }


      const newWidth =
        this.viewport
          .getBoundingClientRect()
          .width;


      /*
       * Ignore vertical-only resize events.
       */
      if (
        Math.abs(
          newWidth -
          this.viewportWidth
        ) < 2
      ) {
        return;
      }


      this.buildMarquee();

    },
    250
  );


  /**
   * Resume only after keyboard focus leaves
   * the complete announcement bar.
   *
   * @param {FocusEvent} event
   */
  handleFocusOut = (
    event
  ) => {

    if (
      event.relatedTarget &&
      this.contains(
        event.relatedTarget
      )
    ) {
      return;
    }


    this.resumeSmoothly();

  };


  /**
   * Browser tab visibility.
   */
  handleVisibilityChange = () => {

    const animation =
      this.getTrackAnimation();


    if (!animation) {
      return;
    }


    if (
      document.hidden
    ) {

      animation.pause();

      return;

    }


    animation.play();


    /*
     * Don't restart if customer
     * currently has mouse over bar.
     */
    if (
      this.matches(':hover')
    ) {

      animation.updatePlaybackRate(
        0
      );

    } else {

      animation.updatePlaybackRate(
        1
      );

    }

  };

}


/**
 * Wait one browser animation frame.
 *
 * @returns {Promise<void>}
 */
function nextFrame() {

  return new Promise(
    (resolve) => {

      requestAnimationFrame(
        () => resolve()
      );

    }
  );

}


/**
 * Smoothly animate a numeric value.
 *
 * Used for:
 *
 * playbackRate 1 → 0
 * playbackRate 0 → 1
 *
 * @param {object} options
 * @param {number} options.from
 * @param {number} options.to
 * @param {number} options.duration
 * @param {(value: number) => void} options.onUpdate
 * @param {(progress: number) => number} [options.easing]
 * @param {() => void} [options.onComplete]
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (t) =>
    t * t * (3 - 2 * t),
  onComplete,
}) {

  const startTime =
    performance.now();


  let cancelled =
    false;


  let currentValue =
    from;


  /**
   * @param {number} currentTime
   */
  function animate(
    currentTime
  ) {

    if (
      cancelled
    ) {
      return;
    }


    const elapsed =
      currentTime -
      startTime;


    const progress =
      Math.min(
        elapsed /
        duration,
        1
      );


    const easedProgress =
      easing(
        progress
      );


    currentValue =
      from +
      (to - from) *
      easedProgress;


    onUpdate(
      currentValue
    );


    if (
      progress < 1
    ) {

      requestAnimationFrame(
        animate
      );

    } else if (
      typeof onComplete ===
      'function'
    ) {

      onComplete();

    }

  }


  requestAnimationFrame(
    animate
  );


  return {

    get current() {

      return currentValue;

    },


    cancel() {

      cancelled =
        true;

    },

  };

}


if (
  !customElements.get(
    'announcement-bar-component'
  )
) {

  customElements.define(
    'announcement-bar-component',
    AnnouncementBar
  );

}